import React, { Fragment, FC } from 'react';
import { Trans } from 'react-i18next';
import { compose } from 'recompose';
import { Table, TableHead, TableBody, TableCell, TableRow } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';

import { Group } from 'api';
import { withUser, IWithUser } from 'providers/UserContext';
import { isGroupOwner } from 'features/groups/helpers';

import { DummyTableRow, EmptyState, GroupRows, IEmptyState, ResponsiveTable } from './';

interface IProps {
  emptyStateName: keyof IEmptyState;
  groups: Group[];
}

const styles = (theme: Theme) =>
  createStyles({
    borderCorrection: {
      width: 'calc(100% - 2px)',
    },
    spacer: {
      minWidth: theme.spacing(8),
    },
  });

interface IPropsEnhanced extends IProps, IWithUser, WithStyles<typeof styles> {}

const enhancers = compose<IPropsEnhanced, IProps>(withUser, withStyles(styles));

const GroupListOwner: FC<IPropsEnhanced> = ({ classes, emptyStateName, groups, user }) => {
  function getRole(group: Group) {
    const isOwner = isGroupOwner(user, group.id);
    return isOwner ? (
      <Trans i18nKey="groups:roleOwner" />
    ) : (
      <Trans i18nKey="groups:roleModerator" />
    );
  }

  if (emptyStateName && !groups.length) {
    return <EmptyState name={emptyStateName} />;
  }

  return (
    <ResponsiveTable>
      <Table padding="none" className={classes.borderCorrection}>
        <TableHead>
          <TableRow>
            <TableCell padding="default">
              <Trans i18nKey="common:name" />
            </TableCell>
            <TableCell align="center" padding="default">
              <Trans i18nKey="common:member_plural" />
            </TableCell>
            <TableCell align="center" padding="default">
              <Trans i18nKey="common:role" />
            </TableCell>
            <TableCell className={classes.spacer} />
          </TableRow>
        </TableHead>
        <TableBody>
          {groups.map((group, index) => (
            <Fragment key={group.id}>
              {!index && <DummyTableRow height={20} colSpan={3} />}
              <GroupRows.Owner group={group} role={getRole(group)} />
              <DummyTableRow height={20} colSpan={3} />
            </Fragment>
          ))}
        </TableBody>
      </Table>
    </ResponsiveTable>
  );
};

export default enhancers(GroupListOwner);
